import React, { useState, useCallback, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption, type ThemedListViewMode } from "@/components/ThemedList";
import { ActivityCard } from "@/components/activity/ActivityCard";
import { CollapsibleFilterSection } from "@/components/filter";
import { useFilterPersistence } from "@/hooks/useFilterPersistence";
import { filterActivities, computeFilteredCounts } from "@/services/unified-filter-service";

import { useFirebase } from "@components/firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { getMyActivity } from "@/services/firebase/activity-functions";
import { ActivityLogModel } from "common247";
import { Spacing } from "@/constants/ThemeColours";

const ACTIVITY_SORT_OPTIONS: ISortOption<ActivityLogModel>[] = [
  { key: "createdAt_desc", label: "Date (Newest First)", getValue: (a) => a.createdAt || 0 },
  { key: "createdAt_asc", label: "Date (Oldest First)", getValue: (a) => a.createdAt || 0, compareFn: (a, b) => (a.createdAt || 0) - (b.createdAt || 0) },
  { key: "targetType", label: "Entity Type", getValue: (a) => a.targetType || "" },
  { key: "action", label: "Action", getValue: (a) => a.action || "" },
  { key: "targetTitle", label: "Title / Name", getValue: (a) => a.targetTitle || a.description || "" },
];

/**
 * Derives a relative date grouping string from an epoch millisecond timestamp.
 */
function getDatePeriodGroup(timestamp: number): string {
  if (!timestamp) return "Unknown Date";
  const now = new Date();
  const date = new Date(timestamp);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  const startOfLast7Days = startOfToday - 7 * 24 * 60 * 60 * 1000;
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

  if (timestamp >= startOfToday) return "Today";
  if (timestamp >= startOfYesterday) return "Yesterday";
  if (timestamp >= startOfLast7Days) return "Past 7 Days";
  if (timestamp >= startOfThisMonth) return "Earlier This Month";

  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

const ACTIVITY_GROUP_OPTIONS: IGroupOption<ActivityLogModel>[] = [
  {
    key: "datePeriod",
    label: "Date Period",
    getGroupValue: (a) => getDatePeriodGroup(a.createdAt),
  },
  {
    key: "targetType",
    label: "Entity Type",
    getGroupValue: (a) => {
      const type = a.targetType || "other";
      return type.charAt(0).toUpperCase() + type.slice(1) + " Activities";
    },
  },
  {
    key: "action",
    label: "Action",
    getGroupValue: (a) => {
      switch (a.action) {
        case "create":
          return "Created Items";
        case "update":
          return "Updated Items";
        case "delete":
          return "Deleted Items";
        case "logon":
        case "signout":
        case "signup":
          return "Authentication & Sessions";
        case "invite_contact":
          return "Contact Invitations";
        default:
          return "Other Actions";
      }
    },
  },
];

/**
 * My Activity Screen: Displays activity log entries for the authenticated user
 * across all objects they own, can edit, or can view.
 */
export default function MyActivityScreen() {
  const services = useFirebase();
  const textMuted = useThemeColor({}, "textMuted");

  const [activities, setActivities] = useState<ActivityLogModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Unified filter persistence
  const { filter, setFilter, resetFilter } = useFilterPersistence("activity");

  const effectiveActivities = useMemo(() => {
    return filterActivities(activities, filter);
  }, [activities, filter]);

  const filteredCounts = useMemo(() => {
    return computeFilteredCounts({ activities }, filter, "activity");
  }, [activities, filter]);

  /**
   * Fetches the user's activity stream from the backend.
   */
  const fetchActivityData = useCallback(async (isRefresh = false) => {
    if (!services) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await getMyActivity(services, { limit: 100, filter });
      if (response && response.status) {
        setActivities(response.activities || []);
      } else {
        CustomAlert.alert("Error", response?.error || "Failed to load activity stream.");
      }
    } catch (error) {
      console.error("Error fetching activity stream:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching activities.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [services, filter]);

  useFocusEffect(
    useCallback(() => {
      fetchActivityData();
    }, [fetchActivityData])
  );

  return (
    <ThemedScreen
      title="My Activity"
      loading={loading}
      scrollable={false}
    >
      <ThemedView style={styles.container}>
        {/* Unified Collapsible Entity Filter */}
        <CollapsibleFilterSection
          screen="activity"
          filter={filter}
          onFilterChange={setFilter}
          counts={filteredCounts}
          onReset={resetFilter}
        />
        <ThemedList<ActivityLogModel>
          data={effectiveActivities}
          renderItem={(item: ActivityLogModel, index: number, isHovered: boolean, viewMode: ThemedListViewMode) => (
            <ActivityCard
              activity={item}
              viewMode={viewMode}
              isHovered={isHovered}
              isTimeline={true}
            />
          )}
          keyExtractor={(item) => item.id || `${item.userId}_${item.createdAt}_${item.action}`}
          sortOptions={ACTIVITY_SORT_OPTIONS}
          defaultSortKey="createdAt_desc"
          defaultSortDirection="desc"
          groupOptions={ACTIVITY_GROUP_OPTIONS}
          defaultGroupKey="datePeriod"
          showViewSelector={false}
          defaultViewMode="full"
          viewMode="full"
          columns={1}
          filterPlaceholder="Search activities, titles, or descriptions..."
          emptyText="No activity found. When objects are created, updated, or modified, activity entries will appear here."
          isRefreshing={refreshing}
          onRefresh={() => fetchActivityData(true)}
          loading={loading}
          enableHover={true}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
